import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, deleteItem } from '../fitness/fitnessApi';

const LIST = 'Roles';

const DEFAULT_ROLES = [
  { Title: 'Fragos', Color: '#4CAF50', SortOrder: 2, IsActive: true },
  { Title: 'Family', Color: '#FF9800', SortOrder: 3, IsActive: true },
];

export function useRoles(getToken, userEmail) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilters, setActiveFilters] = useState(null); // null = all active

  const fetchRoles = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      let items = await getItems(token, LIST);

      // Seed defaults if list is empty
      if (items.length === 0) {
        for (const def of DEFAULT_ROLES) {
          await createItem(token, LIST, { ...def, CreatedBy: userEmail });
        }
        items = await getItems(token, LIST);
      }

      const mapped = items.map(it => ({
        id: it.id,
        name: it.Title || '',
        color: it.Color || '#999',
        sortOrder: it.SortOrder || 0,
        createdBy: it.CreatedBy || '',
        isActive: it.IsActive !== false,
      })).sort((a, b) => a.sortOrder - b.sortOrder);

      setRoles(mapped);

      // Load saved filter prefs from localStorage
      const saved = localStorage.getItem('dh_roleFilters');
      if (saved) {
        try {
          setActiveFilters(JSON.parse(saved));
        } catch { /* ignore */ }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  function toggleFilter(roleId) {
    setActiveFilters(prev => {
      const activeRoleIds = roles.filter(r => r.isActive).map(r => r.id);
      const current = prev || activeRoleIds;
      let next;
      if (current.includes(roleId)) {
        next = current.filter(id => id !== roleId);
        if (next.length === 0) next = activeRoleIds; // don't allow empty — reset to all
      } else {
        next = [...current, roleId];
      }
      localStorage.setItem('dh_roleFilters', JSON.stringify(next));
      return next;
    });
  }

  function isRoleVisible(roleId) {
    if (!activeFilters) return true;
    return activeFilters.includes(roleId);
  }

  async function addRole(name, color) {
    const token = await getToken();
    const maxOrder = roles.reduce((max, r) => Math.max(max, r.sortOrder), 0);
    const created = await createItem(token, LIST, {
      Title: name.trim(),
      Color: color,
      SortOrder: maxOrder + 1,
      CreatedBy: userEmail,
      IsActive: true,
    });
    await fetchRoles();
    return created;
  }

  async function editRole(id, fields) {
    const token = await getToken();
    await updateItem(token, LIST, id, fields);
    await fetchRoles();
  }

  async function removeRole(id) {
    const token = await getToken();
    await deleteItem(token, LIST, id);
    await fetchRoles();
  }

  return {
    roles: roles.filter(r => r.isActive),
    allRoles: roles,
    loading,
    error,
    toggleFilter,
    isRoleVisible,
    activeFilters,
    addRole,
    editRole,
    removeRole,
    refetch: fetchRoles,
  };
}
